import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/link";
import { useQuery } from "@tanstack/react-query";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { getAuthorized } from "@/lib/fetch";
import i18n from "@/i18n";

type K1SchemaV2Row = Record<string, unknown>;

const textCell: ColumnDef<K1SchemaV2Row, unknown>["cell"] = ({ getValue }) => {
  const value = getValue();
  if (value === null || value === undefined) return "";
  return String(value);
};

type RelatedLinkCellProps = {
  id: string;
  href: string;
  itemPath: string;
  initialLabel?: string | null;
  labelKeys: string[];
};

function RelatedLinkCell({
  id,
  href,
  itemPath,
  initialLabel,
  labelKeys,
}: RelatedLinkCellProps) {
  const { data } = useQuery<Record<string, unknown> | null>({
    queryKey: ["k1", "related-item", itemPath, id],
    queryFn: async () => {
      const res = await getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}${itemPath}/${id}`,
      );
      const json = await res.json();
      return (json?.data ?? json ?? null) as Record<string, unknown> | null;
    },
    enabled: !initialLabel,
    staleTime: 5 * 60 * 1000,
  });

  const fetchedLabel =
    labelKeys
      .map((key) => data?.[key])
      .find((value) => value !== null && value !== undefined && value !== "")
      ?.toString() ?? null;

  const label = initialLabel ?? fetchedLabel ?? id;

  return (
    <Link href={toWorkspacePath(href)} className="text-xs" underline="hover">
      {label}
    </Link>
  );
}

const relationNameLinkCell = (
  idAccessorKey: string,
  labelAccessorKeys: string[],
  relatedTablePath: string,
): ColumnDef<K1SchemaV2Row, unknown>["cell"] => {
  return ({ row }) => {
    const rawValue = row.getValue(idAccessorKey);
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return "";
    }

    const id = String(rawValue);
    const initialLabel =
      labelAccessorKeys
        .map((key) => row.original[key])
        .find((value) => value !== null && value !== undefined && value !== "")
        ?.toString() ?? null;

    return (
      <RelatedLinkCell
        id={id}
        href={`/admin/k1/${relatedTablePath}/${id}`}
        itemPath={`/k1/${relatedTablePath}`}
        initialLabel={initialLabel}
        labelKeys={labelAccessorKeys}
      />
    );
  };
};

export const k1_documents_columns: ColumnDef<K1SchemaV2Row, unknown>[] = [
  {
    accessorKey: "id",
    header: "ID",
    cell: ({ row }) => {
      const id = row.getValue("id");
      if (id === null || id === undefined || id === "") return "";
      return (
        <Link
          href={`${window.location.pathname}/${id}`}
          className="text-xs"
          underline="hover"
        >
          {String(id)}
        </Link>
      );
    },
  },
  {
    accessorKey: "fund_id",
    header: "Fund",
    cell: relationNameLinkCell(
      "fund_id",
      ["fund_name", "k1_fund_name"],
      "k1_pe_funds",
    ),
  },
  { accessorKey: "file_name", header: "File name", cell: textCell },
  { accessorKey: "reporting_date", header: "Reporting date", cell: textCell },
  {
    accessorKey: "reporting_period",
    header: "Reporting period",
    cell: textCell,
  },
  {
    accessorKey: "processing_status",
    header: "Processing status",
    cell: textCell,
  },
  { accessorKey: "created_at", header: "Created at", cell: textCell },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => (
      <div className="flex justify-end">
        <Link
          href={`${window.location.pathname}/${row.getValue("id")}`}
          className="text-xs"
          underline="hover"
        >
          {i18n.t("actions.edit")}
        </Link>
      </div>
    ),
  },
];
